import { Box, ButtonGroup, IconButton, MenuItem, Stack, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import Translate from "component/global/translation/translation-component";
import { useTranslation } from "react-i18next";

import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { columnsForTable, isReferenceColumn, tableByName } from "./referencePath";

interface ReferencePathFieldSelectorProps {
    value: string;
    columns: SysColumn[];
    allColumns: SysColumn[];
    tables: SysTable[];
    maxDepth: number;
    onChange: (field: string) => void;
    isColumnSelectable?: (column: SysColumn) => boolean;
    ariaLabel?: string;
    placeholder?: string;
}

export const ReferencePathFieldSelector = ({
    value,
    columns,
    allColumns,
    tables,
    maxDepth,
    onChange,
    isColumnSelectable,
    ariaLabel,
    placeholder,
}: ReferencePathFieldSelectorProps) => {
    const { t } = useTranslation();
    const segments = String(value || "").split(".").filter(Boolean);

    const resolveLevelOptions = (level: number) => {
        if (level === 0) {
            return columns;
        }

        const parentColumn = resolveColumnAtLevel(level - 1);
        if (!isReferenceColumn(parentColumn)) {
            return [];
        }

        const referencedTable = tableByName(tables, parentColumn?.referenced_table_name);
        return columnsForTable(allColumns, referencedTable?.id);
    };

    const resolveColumnAtLevel = (level: number) => {
        let options = columns;
        let selectedColumn: SysColumn | undefined;

        for (let index = 0; index <= level; index += 1) {
            selectedColumn = options.find((column) => column.name === segments[index]);
            if (!selectedColumn || index === level) {
                return selectedColumn;
            }

            const referencedTable = tableByName(tables, selectedColumn.referenced_table_name);
            options = columnsForTable(allColumns, referencedTable?.id);
        }

        return selectedColumn;
    };

    const selectedColumn = segments.length > 0 ? resolveColumnAtLevel(segments.length - 1) : undefined;
    const canExpand = Boolean(isReferenceColumn(selectedColumn) && segments.length < maxDepth);
    const reachedMaxDepth = Boolean(isReferenceColumn(selectedColumn) && segments.length >= maxDepth);

    const updateSegment = (level: number, segment: string) => {
        const nextSegments = [...segments.slice(0, level), segment].filter(Boolean);
        onChange(nextSegments.join("."));
    };

    const expandReference = () => {
        if (!canExpand) {
            return;
        }

        const nextOptions = resolveLevelOptions(segments.length);
        const firstOption = nextOptions[0];
        if (firstOption?.name) {
            onChange([...segments, firstOption.name].join("."));
        }
    };

    const removeLastSegment = () => {
        onChange(segments.slice(0, -1).join("."));
    };

    const levels = Math.max(1, segments.length);

    return (
        <Stack spacing={0.75} sx={{ width: "100%", minWidth: 0 }}>
            <Stack spacing={0.75} sx={{ width: "100%", minWidth: 0 }}>
                {Array.from({ length: levels }).map((_, level) => {
                    const options = resolveLevelOptions(level);
                    const levelValue = segments[level] || "";
                    const isLastLevel = level === levels - 1;
                    const showRemove = isLastLevel && segments.length > 1;
                    const showExpand = isLastLevel && isReferenceColumn(selectedColumn);

                    return (
                        <Stack
                            key={level}
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{
                                width: "100%",
                                minWidth: 0,
                                pl: level > 0 ? { xs: level * 1.5, sm: level * 2 } : 0,
                            }}
                        >
                            {level > 0 && (
                                <Box
                                    sx={{
                                        width: 24,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "text.secondary",
                                        flexShrink: 0,
                                    }}
                                >
                                    <SubdirectoryArrowRightIcon fontSize="small" />
                                </Box>
                            )}
                            <TextField
                                select
                                size="small"
                                value={levelValue}
                                inputProps={{ "aria-label": ariaLabel }}
                                SelectProps={placeholder ? { displayEmpty: true } : undefined}
                                onChange={(event) => updateSegment(level, event.target.value)}
                                sx={{ flex: 1, minWidth: 0 }}
                            >
                                <MenuItem value="">
                                    <em>{level === 0 && placeholder ? placeholder : <Translate text={level === 0 ? "Select field" : "Select referenced field"} />}</em>
                                </MenuItem>
                                {options.map((column) => (
                                    <MenuItem
                                        key={column.name}
                                        value={column.name}
                                        disabled={Boolean(isColumnSelectable && !isColumnSelectable(column) && !isReferenceColumn(column))}
                                    >
                                    {translateMetadataLabel(t, column)}
                                </MenuItem>
                            ))}
                        </TextField>
                        {(showRemove || showExpand) && (
                            <ButtonGroup
                                size="small"
                                variant="outlined"
                                sx={{
                                    flexShrink: 0,
                                    "& .MuiIconButton-root": {
                                        width: 32,
                                        height: 32,
                                        border: 0,
                                        borderRadius: 0,
                                    },
                                }}
                            >
                                {showRemove && (
                                    <Tooltip title={t("Remove referenced field level")}>
                                        <span>
                                        <IconButton size="small" aria-label={String(t("Remove referenced field level"))} title={String(t("Remove referenced field level"))} onClick={removeLastSegment}>
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        </span>
                                    </Tooltip>
                                )}

                                {showExpand && (
                                    <Tooltip title={reachedMaxDepth ? t("Maximum reference depth reached") : t("Expand reference field")}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={String(t("Expand reference field"))}
                                                title={String(t("Expand reference field"))}
                                                onClick={expandReference}
                                                disabled={!canExpand}
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </ButtonGroup>
                        )}
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );
};

export default ReferencePathFieldSelector;
